/**
 * Zentraler API-Client (fetch-Wrapper) gegen die eigene REST-API.
 *
 * AUTH-ANNAHME (Contract Abschnitt 6 lässt Cookie ODER Bearer-JWT offen):
 *  - Standardmäßig wird ein Bearer-Token im Header `Authorization` mitgeschickt
 *    (siehe `AUTH_MODE`). Der Token wird in localStorage gehalten (token-store.ts).
 *  - Für Cookie-Session-Betrieb genügt es, `AUTH_MODE` auf 'cookie' zu setzen:
 *    dann werden keine Auth-Header gesetzt und stattdessen `credentials: 'include'`
 *    verwendet, damit der Session-Cookie mitgeschickt wird.
 *  Beides ist hier zentral umschaltbar, ohne Aufrufer anzufassen.
 */

import type { ApiErrorBody } from './types';
import { getToken } from './token-store';

/** 'bearer' = JWT im Authorization-Header. 'cookie' = Session-Cookie (credentials: include). */
export const AUTH_MODE: 'bearer' | 'cookie' = 'bearer';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  /** zusätzliche Query-Parameter */
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseError(res: Response): Promise<ApiError> {
  let code = `http_${res.status}`;
  let message = res.statusText || 'Unbekannter Fehler';
  try {
    const data = (await res.json()) as Partial<ApiErrorBody>;
    if (data?.error) {
      code = data.error.code ?? code;
      message = data.error.message ?? message;
    }
  } catch {
    // kein JSON-Body — Standardwerte behalten
  }
  return new ApiError(res.status, code, message);
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = opts;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  if (AUTH_MODE === 'bearer') {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: AUTH_MODE === 'cookie' ? 'include' : 'same-origin',
    signal,
  });

  if (!res.ok) throw await parseError(res);

  // 204 No Content o. Ä. ohne Body
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    apiRequest<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  del: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};
